import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { DataGrid } from '@mui/x-data-grid';
import { trTR } from '@mui/x-data-grid/locales';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'src/utils/axios';
import ExcelFileCreator from 'src/components/ExcelFileCreator';

import SectionCard from 'src/components/BpoMutabakat/SectionCard';
import InfoNote from 'src/components/BpoMutabakat/InfoNote';
import { getLevelColor, getSeriesColors } from 'src/components/BpoMutabakat/colors';
import { formatDuration, formatNumber, formatPercent } from 'src/components/BpoMutabakat/format';
import { reportName } from 'src/components/BpoMutabakat/reports';

const KIRILIMLAR = [
  { reportType: 10, label: 'Skill bazında' },
  { reportType: 11, label: 'Agent bazında' }
];

const Outbound = ({ date }) => {
  const theme = useTheme();
  const colors = getSeriesColors(theme);

  const [reportType, setReportType] = useState(10);
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  const [dialer, setDialer] = useState(null);
  const [international, setInternational] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;

    setLoading(true);
    setError('');

    Promise.allSettled([
      axios.get(`/GetBpoMutabakatOutbound/${date}/${reportType}`),
      axios.get(`/GetBpoMutabakatCallResults/${date}`),
      axios.get(`/GetBpoMutabakatDialer/${date}`),
      axios.get(`/GetBpoMutabakatInternational/${date}`)
    ])
      .then((all) => {
        if (cancelled) return;
        const [tablo, sonuc, kirilim, yurtdisi] = all;
        const dizi = (r) =>
          r.status === 'fulfilled' && Array.isArray(r.value?.data) ? r.value.data : [];

        setRows(dizi(tablo));
        setResults(dizi(sonuc));
        setInternational(dizi(yurtdisi));
        setDialer(kirilim.status === 'fulfilled' ? kirilim.value?.data || null : null);

        if (all.every((r) => r.status === 'rejected')) {
          setError(
            all.every((r) => r.reason?.response?.status === 404)
              ? 'Outbound servisleri henüz yayında değil.'
              : 'Veriler alınamadı. Lütfen tekrar deneyin.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date, reportType]);

  /* ------------------------------------------ arama sonucu dağılımı (RT 8) */
  const resultChart = useMemo(() => ({
    series: [{ name: 'Çağrı', data: results.map((r) => r.count) }],
    options: {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: theme.typography.fontFamily,
        background: 'transparent'
      },
      theme: { mode: theme.palette.mode },
      colors: [colors[0]],
      plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '62%' } },
      dataLabels: {
        enabled: true,
        formatter: (v) => formatNumber(v),
        style: { fontSize: '11px', fontWeight: 400 },
        offsetX: 28
      },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
      xaxis: {
        categories: results.map((r) => r.result),
        labels: { formatter: (v) => formatNumber(Math.round(v)) }
      },
      yaxis: { labels: { style: { fontSize: '11px' } } },
      tooltip: { theme: theme.palette.mode, y: { formatter: (v) => formatNumber(v) } }
    }
  }), [results, theme, colors]);

  /* --------------------------------------- dialer / manuel kırılımı (RT 7) */
  const dialerChart = useMemo(() => {
    const items = dialer
      ? [
          { label: 'Dialer', value: dialer.dialerCalls || 0 },
          { label: 'Manuel arama', value: dialer.manualCalls || 0 }
        ]
      : [];
    return {
      series: items.map((i) => i.value),
      options: {
        chart: {
          type: 'donut',
          fontFamily: theme.typography.fontFamily,
          background: 'transparent'
        },
        theme: { mode: theme.palette.mode },
        labels: items.map((i) => i.label),
        colors,
        stroke: { width: 0 },
        dataLabels: { enabled: false },
        legend: { position: 'bottom', fontSize: '12px' },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                value: { fontSize: '20px', fontWeight: 500, formatter: (v) => formatNumber(v) },
                total: {
                  show: true,
                  label: 'Outbound çağrı',
                  fontSize: '12px',
                  formatter: (w) =>
                    formatNumber(w.globals.seriesTotals.reduce((a, b) => a + b, 0))
                }
              }
            }
          }
        },
        tooltip: { theme: theme.palette.mode, y: { formatter: (v) => formatNumber(v) } }
      }
    };
  }, [dialer, theme, colors]);

  const columns = [
    { field: 'department', headerName: 'Departman', flex: 1, minWidth: 150 },
    { field: 'skill', headerName: 'Skill', flex: 1.3, minWidth: 180 },
    { field: 'staffId', headerName: 'Sicil', width: 96 },
    { field: 'agent', headerName: 'Agent', flex: 1.2, minWidth: 170 },
    {
      field: 'calls',
      headerName: 'Dialer çağrı',
      type: 'number',
      width: 118,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'talkSec',
      headerName: 'Görüşme',
      type: 'number',
      width: 110,
      valueFormatter: (v) => formatDuration(v)
    },
    {
      field: 'avgTalkSec',
      headerName: 'Ort. görüşme',
      type: 'number',
      width: 126,
      valueFormatter: (v) => formatDuration(v)
    },
    {
      field: 'holdSec',
      headerName: 'Bekletme',
      type: 'number',
      width: 110,
      valueFormatter: (v) => formatDuration(v)
    },
    {
      field: 'ringSec',
      headerName: 'Çalma',
      type: 'number',
      width: 100,
      valueFormatter: (v) => formatDuration(v)
    },
    {
      field: 'acwSec',
      headerName: 'ACW',
      type: 'number',
      width: 100,
      valueFormatter: (v) => formatDuration(v)
    }
  ];

  /* Skill kırılımında agent kolonları boştur, agent kırılımında doludur. */
  const columnVisibility = { staffId: reportType === 11, agent: reportType === 11 };

  const exportToExcel = () => {
    ExcelFileCreator(
      rows.map((r) => ({
        Departman: r.department,
        Skill: r.skill,
        Sicil: r.staffId,
        Agent: r.agent,
        'Dialer çağrı': r.calls,
        'Görüşme sn': r.talkSec,
        'Ort. görüşme sn': r.avgTalkSec,
        'Bekletme sn': r.holdSec,
        'Çalma sn': r.ringSec,
        'ACW sn': r.acwSec
      })),
      `BPO Mutabakat - Outbound ${date}`
    );
  };

  const ok = getLevelColor(theme, 'good');
  const idle = getLevelColor(theme, 'empty');
  const totalResults = results.reduce((a, r) => a + r.count, 0);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 7 }}>
        <SectionCard
          title="Arama sonucu dağılımı"
          subtitle={`${reportName(8)} · ${formatNumber(totalResults)} çağrı`}
        >
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <ReactApexChart
              options={resultChart.options}
              series={resultChart.series}
              type="bar"
              height={Math.max(240, results.length * 40 + 60)}
            />
          )}
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 5 }}>
        <SectionCard title="Dialer / manuel kırılımı" subtitle={reportName(7)}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <>
              <ReactApexChart
                options={dialerChart.options}
                series={dialerChart.series}
                type="donut"
                height={280}
              />
              {dialer && (
                <InfoNote sx={{ mt: 1 }}>
                  Aynı rapor inbound tarafını da veriyor:{' '}
                  <b>{formatNumber(dialer.inboundCalls)}</b> gelen çağrı,{' '}
                  <b>{formatDuration(dialer.inboundTalkSec)}</b> toplam görüşme.
                </InfoNote>
              )}
            </>
          )}
        </SectionCard>
      </Grid>

      <Grid size={12}>
        <SectionCard
          title="Outbound arama"
          subtitle={`${reportName(reportType)} · ${formatNumber(rows.length)} satır`}
          action={
            <>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={reportType}
                onChange={(e, value) => {
                  if (value !== null) setReportType(value);
                }}
                sx={{
                  '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.4 },
                  '& .MuiToggleButton-root.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'primary.dark' }
                  }
                }}
              >
                {KIRILIMLAR.map((k) => (
                  <ToggleButton key={k.reportType} value={k.reportType}>
                    {k.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={exportToExcel}
                disabled={loading || !rows.length}
              >
                Excel
              </Button>
            </>
          }
          padded={false}
        >
          <Box sx={{ height: 460, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              columnVisibilityModel={columnVisibility}
              loading={loading}
              density="compact"
              disableRowSelectionOnClick
              localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{ border: 0 }}
            />
          </Box>
        </SectionCard>
      </Grid>

      <Grid size={12}>
        <SectionCard
          title="Yurtdışı outbound"
          subtitle={`${reportName(9)} · ${formatNumber(international.length)} çağrı`}
          padded={false}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Çağrı ID</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Takım</TableCell>
                  <TableCell>Aranan numara</TableCell>
                  <TableCell>Sonuç</TableCell>
                  <TableCell align="right">Çalma</TableCell>
                  <TableCell align="right">Görüşme</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {international.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="caption" color="text.secondary">
                        {loading ? 'Yükleniyor…' : 'Bu gün yurtdışı outbound çağrı yok.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {international.map((c) => {
                  const gorusuldu = (c.talkSec || 0) > 0;
                  const renk = gorusuldu ? ok : idle;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{c.id}</TableCell>
                      <TableCell>
                        {c.staffId} · {c.agent}
                      </TableCell>
                      <TableCell>{c.team}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{c.number}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: '2px',
                            borderRadius: 5,
                            fontSize: '0.72rem',
                            fontWeight: 500,
                            color: renk.text,
                            backgroundColor: renk.bg
                          }}
                        >
                          {c.result || c.answerStatus || '—'}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {c.ringSec} sn
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {gorusuldu ? formatDuration(c.talkSec) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <Box sx={{ p: 2 }}>
            <InfoNote>
              {error ? (
                <b>{error}</b>
              ) : (
                <>
                  {reportName(9)}, {reportName(8)} ile <b>aynı tabloda</b> durur; ayrım{' '}
                  <code>REPORT_TYPE</code> kolonundadır. Günlük hacmi tek haneli olduğu için ayrı
                  gösteriliyor &mdash; outbound toplamlarının içinde kaybolmaması gerekiyor.
                  {results.length > 0 && (
                    <>
                      {' '}Sonuç dağılımında &ldquo;görüşüldü&rdquo; etiketiyle gerçekten görüşme
                      süresi olan çağrı sayısı ayrı tutulur; ikisi{' '}
                      {formatPercent(
                        (results.reduce((a, r) => a + (r.talked || 0), 0) /
                          (totalResults || 1)) *
                          100
                      )}{' '}
                      oranında örtüşüyor.
                    </>
                  )}
                </>
              )}
            </InfoNote>
          </Box>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

Outbound.propTypes = {
  date: PropTypes.string
};

export default Outbound;
